import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "cookie_consent";

type ConsentState = "pending" | "accepted" | "rejected" | "partial";

const SellCookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
    // If previously accepted, load GA
    if (consent === "accepted" || consent === "partial_analytics") {
      loadGTM();
    }
  }, []);

  const loadGTM = () => {
    const GTM_ID = "GTM-KX7N5S2L";
    if (document.querySelector(`script[src*="${GTM_ID}"]`)) return;

    const script = document.createElement("script");
    script.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${GTM_ID}');
    `;
    document.head.appendChild(script);
  };

  const accept = (type: "all" | "partial" | "reject") => {
    if (type === "all") {
      localStorage.setItem(CONSENT_KEY, "accepted");
      loadGTM();
    } else if (type === "partial") {
      const key = analyticsEnabled ? "partial_analytics" : "rejected";
      localStorage.setItem(CONSENT_KEY, key);
      if (analyticsEnabled) loadGTM();
    } else {
      localStorage.setItem(CONSENT_KEY, "rejected");
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-card/95 backdrop-blur-xl border border-border rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 shrink-0">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm sm:text-base font-bold text-foreground">
                    🍪 Respect de votre vie privée
                  </h3>
                  <button
                    onClick={() => accept("reject")}
                    className="p-1 rounded-lg hover:bg-muted transition-colors"
                    aria-label="Fermer"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 leading-relaxed">
                  Nous utilisons des cookies pour analyser le trafic et améliorer votre expérience. 
                  Conformément au RGPD, vous pouvez choisir les cookies que vous acceptez. 
                  Aucune donnée personnelle n'est partagée avec des tiers sans votre consentement.{" "}
                  <a href="/legal#cookies" className="text-primary hover:underline underline-offset-2">Politique de confidentialité</a>
                </p>

                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden mb-4"
                    >
                      <div className="space-y-3 p-4 bg-muted/50 rounded-xl">
                        {/* Necessary */}
                        <label className="flex items-center gap-3 cursor-not-allowed">
                          <input type="checkbox" checked disabled className="accent-primary w-4 h-4" />
                          <div>
                            <span className="text-sm font-medium text-foreground">Cookies essentiels</span>
                            <p className="text-xs text-muted-foreground">Nécessaires au fonctionnement du site (toujours actifs)</p>
                          </div>
                        </label>
                        {/* Analytics */}
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={analyticsEnabled}
                            onChange={() => setAnalyticsEnabled(!analyticsEnabled)}
                            className="accent-primary w-4 h-4"
                          />
                          <div>
                            <span className="text-sm font-medium text-foreground">Cookies analytiques</span>
                            <p className="text-xs text-muted-foreground">Google Analytics — statistiques anonymes de visites</p>
                          </div>
                        </label>
                        {/* Marketing */}
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={marketingEnabled}
                            onChange={() => setMarketingEnabled(!marketingEnabled)}
                            className="accent-primary w-4 h-4"
                          />
                          <div>
                            <span className="text-sm font-medium text-foreground">Cookies marketing</span>
                            <p className="text-xs text-muted-foreground">Publicités personnalisées et remarketing (désactivé)</p>
                          </div>
                        </label>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={() => accept("all")}
                    className="bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-sm"
                  >
                    Tout accepter
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (showDetails) {
                        accept("partial");
                      } else {
                        setShowDetails(true);
                      }
                    }}
                    className="border-primary/30"
                  >
                    {showDetails ? "Confirmer mes choix" : "Personnaliser"}
                  </Button>
                  <button
                    onClick={() => accept("reject")}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                  >
                    Tout refuser
                  </button>
                  <div className="ml-auto hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Shield className="w-3.5 h-3.5 text-primary" />
                    Conforme RGPD
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SellCookieConsent;
